import type { PostHog } from "posthog-js";
import type { AccountType, SignupRole } from "../types/types";

type AnalyticsClient = Pick<PostHog, "capture"> | null | undefined;

type BaseSignupProperties = {
  role: SignupRole;
  account_type: AccountType;
  has_guardian_invite: boolean;
};

export type AdultGigCreateFlowSource = "adult_dashboard" | "adult_posted_gigs" | "adult_reviews";
export type AdultGigCreateFlowEntryPoint =
  | "dashboard_hero"
  | "dashboard_empty_posted_gigs"
  | "posted_gigs_page_header"
  | "posted_gigs_empty_state"
  | "posted_gigs_draft_card"
  | "posted_gigs_published_card"
  | "posted_gigs_expired_card"
  | "adult_reviews_completed_gig";
export type AdultGigCreateFlowMode = "create" | "draft" | "published" | "repost";
export type AdultGigCreateFlowStep = "BASICS" | "PAYMENT" | "WORK" | "TIMING";
export type AdultGigCreateFlowAction = "continue" | "submit";

export type AdultGigCreateFlowAnalyticsContext = {
  flow_id: string;
  source: AdultGigCreateFlowSource;
  entry_point?: AdultGigCreateFlowEntryPoint;
  mode: AdultGigCreateFlowMode;
  guided_flow: boolean;
};

type AdultGigCreateFlowStepProperties = AdultGigCreateFlowAnalyticsContext & {
  step: AdultGigCreateFlowStep;
  step_index: number;
  step_count: number;
};

type AdultGigCreateFlowProperties = {
  flow_id: string;
  source: AdultGigCreateFlowSource;
  step: AdultGigCreateFlowStep;
  step_index: number;
  step_count: number;
  can_create_volunteer_gig: boolean;
  location_type: "IN_PERSON" | "REMOTE";
  volunteer: boolean;
  has_started_input: boolean;
  has_title: boolean;
  has_category: boolean;
  has_location: boolean;
  has_payment_amount: boolean;
  has_description: boolean;
  has_images: boolean;
  image_count: number;
  estimated_duration_minutes: number | null;
};

type AdultGigCreateFlowInputProperties = AdultGigCreateFlowProperties & {
  first_field:
    | "category"
    | "title"
    | "location_type"
    | "city"
    | "state"
    | "zip"
    | "payment_amount"
    | "volunteer"
    | "duration"
    | "description"
    | "requires_equipment"
    | "equipment_description"
    | "images"
    | "due_date"
    | "custom_schedule_date_mode"
    | "schedule_date"
    | "preferred_time"
    | "custom_preferred_time"
    | "flexible_timing_details";
};

export const captureUserLoggedIn = (client: AnalyticsClient, properties: { roles: string[] }) => {
  client?.capture("user_logged_in", properties);
};

export const captureUserLoggedOut = (client: AnalyticsClient) => {
  client?.capture("user_logged_out");
};

export const captureSignupRoleSelected = (client: AnalyticsClient, properties: { role: SignupRole; source: "signup_form" }) => {
  client?.capture("signup_role_selected", properties);

  if (properties.role === "TEEN") {
    client?.capture("teen_signup_role_selected", { source: properties.source });
  }
};

export const captureUserSignedUp = (client: AnalyticsClient, properties: BaseSignupProperties) => {
  client?.capture("user_signed_up", properties);

  if (properties.role === "TEEN") {
    client?.capture("teen_signup_completed", {
      account_type: properties.account_type,
      has_guardian_invite: properties.has_guardian_invite,
    });
  }

  if (properties.role === "ADULT") {
    client?.capture("adult_signup_completed", {
      account_type: properties.account_type,
      has_guardian_invite: properties.has_guardian_invite,
    });

    if (properties.account_type === "BUSINESS") {
      client?.capture("adult_business_signup_completed", {
        has_guardian_invite: properties.has_guardian_invite,
      });
    } else {
      client?.capture("adult_personal_signup_completed", {
        has_guardian_invite: properties.has_guardian_invite,
      });
    }
  }
};

export const captureTeenProfileUpdated = (
  client: AnalyticsClient,
  properties: {
    has_bio: boolean;
    skills_count: number;
    has_profile_photo: boolean;
  },
) => {
  client?.capture("teen_profile_updated", properties);

  if (properties.has_bio && properties.skills_count > 0) {
    client?.capture("teen_profile_completed", properties);
  }
};

export const captureTeenProfilePhotoUploaded = (client: AnalyticsClient, properties: { source: "signup" | "settings" }) => {
  client?.capture("teen_profile_photo_uploaded", properties);
};

export const captureGigApplied = (client: AnalyticsClient, properties: { gig_id: string; source?: "gig_board" | "teen_gigs" }) => {
  client?.capture("gig_applied", properties);
  client?.capture("teen_application_submitted", {
    application_type: "gig",
    gig_id: properties.gig_id,
    source: properties.source,
  });
};

export const captureBusinessListingApplied = (
  client: AnalyticsClient,
  properties: { listing_id: string; source?: "business_listing_board" },
) => {
  client?.capture("business_listing_applied", properties);
  client?.capture("teen_application_submitted", {
    application_type: "business_listing",
    listing_id: properties.listing_id,
    source: properties.source,
  });
};

export const captureGigRequestWithdrawn = (client: AnalyticsClient, properties: { request_id: string }) => {
  client?.capture("gig_request_withdrawn", properties);
};

export const captureGigAppliedAgain = (client: AnalyticsClient, properties: { gig_id: string }) => {
  client?.capture("gig_applied_again", properties);
};

export const captureTeenScheduleProposalSubmitted = (client: AnalyticsClient, properties: { request_id: string }) => {
  client?.capture("teen_schedule_proposal_submitted", properties);
};

export const captureTeenSuggestedTimeConfirmed = (
  client: AnalyticsClient,
  properties: { request_id: string; gig_title: string | null },
) => {
  client?.capture("teen_suggested_time_confirmed", properties);
};

export const captureTeenAcceptedForGig = (
  client: AnalyticsClient,
  properties: { gig_id: string; request_id: string; gig_type: "funded_open" | "volunteer" | "multi_teen_selection" },
) => {
  client?.capture("teen_accepted_for_gig", properties);
};

export const captureTeenRejectedForGig = (client: AnalyticsClient, properties: { gig_id: string; request_id: string }) => {
  client?.capture("teen_rejected_for_gig", properties);
};

export const captureGigFundingCheckoutStarted = (
  client: AnalyticsClient,
  properties: {
    gig_id: string;
    request_id?: string | null;
    compensation_cents: number;
    checkout_type?: "single_request" | "combined_crew";
    selected_seat_count?: number;
  },
) => {
  client?.capture("gig_funding_checkout_started", properties);
};

export const captureAdultScheduleProposalSent = (client: AnalyticsClient, properties: { request_id: string; gig_id: string }) => {
  client?.capture("adult_schedule_proposal_sent", properties);
};

export const captureAdultGuardianRequestSent = (
  client: AnalyticsClient,
  properties: { pending_signup: boolean; source: "adult_guardian_workspace" },
) => {
  client?.capture("adult_guardian_request_sent", properties);
};

export const captureAdultGuardianRequestDecided = (
  client: AnalyticsClient,
  properties: { decision: "ACCEPTED" | "REJECTED"; source: "adult_guardian_workspace" },
) => {
  client?.capture("adult_guardian_request_decided", properties);

  if (properties.decision === "ACCEPTED") {
    client?.capture("adult_guardian_connection_completed", {
      source: properties.source,
    });
  }
};

export const captureAdultGigCreateModalOpened = (
  client: AnalyticsClient,
  properties: AdultGigCreateFlowAnalyticsContext & {
    has_initial_values: boolean;
    restrict_published_fields: boolean;
  },
) => {
  client?.capture("adult_gig_create_modal_opened", properties);
};

export const captureAdultGigCreateStepViewed = (
  client: AnalyticsClient,
  properties: AdultGigCreateFlowStepProperties,
) => {
  client?.capture("adult_gig_create_step_viewed", properties);
};

export const captureAdultGigCreateStepCompleted = (
  client: AnalyticsClient,
  properties: AdultGigCreateFlowStepProperties & {
    next_step: AdultGigCreateFlowStep;
  },
) => {
  client?.capture("adult_gig_create_step_completed", properties);
};

export const captureAdultGigCreateValidationFailed = (
  client: AnalyticsClient,
  properties: AdultGigCreateFlowStepProperties & {
    action: AdultGigCreateFlowAction;
    error_code: string;
  },
) => {
  client?.capture("adult_gig_create_validation_failed", properties);
};

export const captureAdultGigCreateSubmitted = (
  client: AnalyticsClient,
  properties: AdultGigCreateFlowAnalyticsContext & {
    status: "DRAFT" | "PUBLISHED";
    category: string;
    volunteer: boolean;
    schedule_type: string;
    location_type: string;
    has_images: boolean;
    image_count: number;
    teens_required: number;
    poster_payment_amount_cents: number | null;
    estimated_duration_minutes: number | null;
  },
) => {
  client?.capture("adult_gig_create_submitted", properties);
};

export const captureAdultGigCreateModalClosed = (
  client: AnalyticsClient,
  properties: AdultGigCreateFlowStepProperties & {
    reason: "cancelled" | "submitted";
    completed: boolean;
    elapsed_seconds: number | null;
  },
) => {
  client?.capture("adult_gig_create_modal_closed", properties);
};

export type AdultGigPublishOutcomeProperties = {
  gig_id: string;
  status: "DRAFT" | "PUBLISHED";
  source: AdultGigCreateFlowSource;
  flow_id?: string;
  entry_point?: AdultGigCreateFlowEntryPoint;
  mode?: AdultGigCreateFlowMode;
  category: string;
  volunteer: boolean;
  schedule_type: string;
  has_images: boolean;
  image_count: number;
  teens_required?: number;
  poster_payment_amount_cents: number | null;
};

export const captureAdultGigPublishOutcome = (
  client: AnalyticsClient,
  properties: AdultGigPublishOutcomeProperties,
) => {
  if (properties.status === "PUBLISHED") {
    client?.capture("adult_gig_published", properties);
  } else {
    client?.capture("adult_gig_draft_saved", properties);
  }
};

export const captureAdultGigCreateFlowOpened = (client: AnalyticsClient, properties: AdultGigCreateFlowProperties) => {
  client?.capture("adult_gig_create_flow_opened", properties);
};

export const captureAdultGigCreateFlowInputStarted = (
  client: AnalyticsClient,
  properties: AdultGigCreateFlowInputProperties,
) => {
  client?.capture("adult_gig_create_flow_input_started", properties);
};

export const captureAdultGigCreateFlowStepViewed = (client: AnalyticsClient, properties: AdultGigCreateFlowProperties) => {
  client?.capture("adult_gig_create_flow_step_viewed", properties);

  if (properties.step === "PAYMENT") {
    client?.capture("adult_gig_create_payment_step_viewed", properties);
  }
};

export const captureAdultGigCreateFlowAbandoned = (client: AnalyticsClient, properties: AdultGigCreateFlowProperties) => {
  client?.capture("adult_gig_create_flow_abandoned", properties);

  if (properties.step === "PAYMENT" && properties.has_started_input) {
    client?.capture("adult_gig_create_payment_abandoned", properties);
  }
};

export const captureAdultGigCreated = (
  client: AnalyticsClient,
  properties: AdultGigPublishOutcomeProperties,
) => {
  client?.capture("adult_gig_created", properties);
  captureAdultGigPublishOutcome(client, properties);
};

export const captureBusinessListingDraftSaved = (
  client: AnalyticsClient,
  properties: {
    listing_id: string;
    source: "adult_dashboard" | "business_listings_workspace";
    category: string;
    job_type: string;
    pay_type: string;
    min_pay_amount_cents: number;
    max_pay_amount_cents: number;
  },
) => {
  client?.capture("business_listing_draft_saved", properties);
};

export const captureBusinessListingPublicationCheckoutStarted = (
  client: AnalyticsClient,
  properties: {
    source: "adult_dashboard" | "business_listings_workspace";
    category: string;
    job_type: string;
    pay_type: string;
    min_pay_amount_cents: number;
    max_pay_amount_cents: number;
  },
) => {
  client?.capture("business_listing_publication_checkout_started", properties);
};

export const captureBusinessListingPublished = (
  client: AnalyticsClient,
  properties: { listing_id: string; source: "business_listings_workspace" },
) => {
  client?.capture("business_listing_published", properties);
};

export const captureTeenReviewSubmitted = (
  client: AnalyticsClient,
  properties: { request_id: string; gig_id: string | null; rating: number },
) => {
  client?.capture("teen_review_submitted", properties);
};

export const captureFeedbackSubmitted = (
  client: AnalyticsClient,
  properties: { feedback_type: string; source_path: string },
) => {
  client?.capture("feedback_submitted", properties);
};
